/* @flow */
import React, { useRef } from "react";
import { connect } from "react-redux";
import { Route, withRouter } from "react-router-dom";
import { getViewedStock } from "../../redux/reducer/tabs";
import { getServerTime } from "../../redux/reducer/points";
import { getActiveDeals } from "../../redux/reducer/deals";
import MobileLayout from "../TradeMobile/TradeMobileLayout";
import connected from "../common/connected";
import StatelessHeader from "../TradeHeader";
import StatelessChart from "../Chart";
import StatelessControls from "../TradeMobile/TradeControlTabs";
import StatelessNews from "../News";
import AdsBlock from "../TradeMobile/AdsBlock";
import Tools from "../TradeMobile/Toolbar/ToolsPopup";
import Assets from "../TradeMobile/Toolbar/Assets";
import SocialTradingMobile from "../SocialTrading/Mobile";
// import SwipeToZoom from "../TradeMobile/SwipeToZoom";
import StatelessPopupDeposit from "../PopupDeposit/PopupDeposit";
import StatelessInsufficientPopup from "../InsufficientPopup/InsufficientPopup";
import StatelessVerificationRequiredPopup from "../TradeModals/VerificationRequired/VerificationRequired";
import StatelessTradeIsClosingPopup from "../TradeModals/TradeIsClosing";
import StatelessIncreaseBidLimitPopup from "../TradeModals/IncreaseBidLimit";
import DemoTournamentPopup from "../TradeModals/DemoTournamentPopup";
import StockClose from "./StockClose";
import { mapStateToGettext } from "../common/Gettext";
import Tour from "../Tour";
import BonusTimer from "./BonusTimer";
import ABTest from "../ABTest";
import withErrorCatch from "../withErrorCatch";

import type { State } from "../../redux/reducer";
import type { Dispatch } from "../../redux/action";
import type { LayoutProps } from "../TradingPlatform";

// Попробуем подключать каждый подкомпонент
// по-отдельности. В теории это должно дать
// некий прирост производительности. К тому
// же "вёрсточка" выглядит чище!

// Красота в том, что оборачиваемый
// компонент всегда в первой строке:

const ChartArea = connected(StatelessChart)((state: State, ownProps) => {
  const stock = getViewedStock(state.tabs);
  const time = getServerTime(stock, state.points) || 0;
  const deals =
    typeof ownProps.deals === "object" &&
    ownProps.deals.type === "SPECIAL TOUR DEAL"
      ? ownProps.deals.deals
      : getActiveDeals(stock, time, ownProps.deals);

  return {
    key: stock,
    tradeMode: state.settings.mode,
    points: ownProps.points || state.points[stock] || [],
    settings: { ...state.settings, ...ownProps.settings },
    stats: state.stats[stock],
    profile: state.profile,
    deals: deals
  };
});

const News = connected(StatelessNews)(
  (state: State) => ({
    news: state.news,
    soundsEnabled: state.audio.enabled
  }),
  (dispatch: Dispatch) => ({
    onDismiss(id) {
      dispatch({
        type: "DISMISS_NEWS",
        payload: id
      });
    }
  })
);

const Header = connected(StatelessHeader)((state: State) => ({
  profile: state.profile,
  accounts: state.accounts
}));

const InsufficientPopup = connected(StatelessInsufficientPopup)(
  (state: State) => {
    const activeAccount = state.accounts.find(account => {
      return state.profile
        ? account.id === state.profile.active_account_id
        : account.isActive;
    });

    return {
      profileId: state.profile && state.profile.id,
      initialDeposit: state.remoteTradeSettings.initial,
      minFillup: state.remoteTradeSettings.minFillup,
      isActiveAccountReal: activeAccount ? activeAccount.type === "real" : false
    };
  }
);

const PopupDeposit = connected(StatelessPopupDeposit)((state: State) => {
  const activeAccount = state.accounts.find(account => {
    return state.profile
      ? account.id === state.profile.active_account_id
      : account.isActive;
  });

  return {
    profileId: state.profile && state.profile.id,
    accounts: state.accounts,
    initialDeposit: state.remoteTradeSettings.initial,
    minFillup: state.remoteTradeSettings.minFillup,
    isActiveAccountReal: activeAccount ? activeAccount.type === "real" : false
  };
});

const VerificationRequiredPopup = connected(StatelessVerificationRequiredPopup)(
  (state: State) => ({
    validation: state.profile
      ? state.profile.validation
      : { phone: 0, email: 0, profile: 0, passport: 0 }
  })
);

const IncreaseBidLimitPopup = connected(StatelessIncreaseBidLimitPopup)(
  (state: State) => {
    const { profile } = state;

    let bid_limit = 5000;

    if (profile) {
      if (profile.bid_limit) {
        bid_limit =
          typeof profile.bid_limit === "object"
            ? profile.bid_limit[profile.currency]
            : profile.bid_limit;
      }
    }

    return {
      bid_limit: bid_limit
    };
  }
);

const TradeIsClosingPopup = connected(StatelessTradeIsClosingPopup)(
  (state: State) => {
    //
  }
);

const Controls = connected(StatelessControls)((state: State, ownProps) => {
  const stock = getViewedStock(state.tabs);
  const points = state.points[stock] || [];
  let bids = state.remoteTradeSettings.bids;
  const currency = state.remoteTradeSettings.currency;
  let maxBidVolume;
  if (state.profile) {
    if (typeof state.profile.bid_limit === "object") {
      maxBidVolume = state.profile.bid_limit[currency];
    } else {
      maxBidVolume = state.profile.bid_limit;
    }
  }
  if (!maxBidVolume && state.apiConfigs) {
    maxBidVolume = state.apiConfigs.maxBidVolume[currency];
  }
  if (bids && bids.length && bids[bids.length - 1] > maxBidVolume) {
    bids = bids.filter(bid => bid <= maxBidVolume);
  }

  const openDeals = deal =>
    deal.time !== undefined && deal.profit === undefined;
  const stocksWithOperations = ownProps.deals
    .filter(openDeals)
    .reduce((res, deal) => {
      if (deal) {
        res.push(deal.stock);
      }
      return res;
    }, []);

  const pointsByStock = stocksWithOperations.reduce((res, stock) => {
    const points = state.points[stock];
    if (points && !res[stock]) {
      res[stock] = points.length > 0 ? points[points.length - 1] : null;
    }

    return res;
  }, {});

  return {
    tradeMode: state.settings.mode,
    point: points.length > 0 ? points[points.length - 1] : null,
    pointsByStock,
    percentBids: state.remoteTradeSettings.percentBids,
    percentBidThreshold: state.remoteTradeSettings.percentBidThreshold,
    suggestedBids: bids,
    currentBid: state.settings.currentBid,
    maxBidVolume: maxBidVolume || (bids && bids.length && bids[bids.length - 1]),
    now: state.time,
    isIOS: state.serverData ? state.serverData.isIOS : false,
    ...mapStateToGettext(state)
  };
});

let MobileTradeScreen = (props: LayoutProps) => {
  let chartRef = useRef(null);
  let tradeControlsRef = useRef(null);

  const isStockClose =
    !props.stock.enabled || props.timeUntilNextLockToggle === 0;

  const closeDealClickMobileHandler = () => {
    if (
      tradeControlsRef.current &&
      typeof tradeControlsRef.current.setActiveTab === "function"
    ) {
      tradeControlsRef.current.setActiveTab("deals");
    }
  };

  return (
    <MobileLayout
      isIOS={props.isIOS}
      appVersion={props.appVersion}
      showControls={!isStockClose}
      dealsToCompensate={props.dealsToCompensate}
      onCompensation={props.onCompensation}
      compensationUpdated={props.compensationUpdated}
      compensationCreated={props.compensationCreated}
      onCompensationCreated={props.onCompensationCreated}
      activeAccount={props.activeAccount}
      tabs={
        <Header
          onProfileUpdate={props.refreshProfile}
          isRefillFetching={props.isRefillFetching}
          accountsReady={props.accountsReady}
          dealsReady={props.dealsReady}
          isFetchingOpenOperation={props.isFetchingOpenOperation}
          onRefillDemoClick={props.refillDemoAccount}
          deals={props.deals}
        />
      }
      bonus={
        <ABTest campaign="first_deposit_bonus">
          <BonusTimer />
        </ABTest>
      }
      controls={
        <Controls
          controlsRef={tradeControlsRef}
          onSubmit={props.tryMakingADeal}
          showIncreaseBidLimitMessage={props.showIncreaseBidLimitMessage}
          showTournamentRefill={props.showTournamentRefill}
          disabled={isStockClose || props.tradeControlsDisabled}
          accountType={props.accountType}
          activeAccount={props.activeAccount}
          possibleIntervals={props.possibleTradeIntervals}
          currentInterval={props.currentTradeInterval}
          stock={props.stock}
          timeUntilNextLockToggle={props.timeUntilNextLockToggle}
          deals={props.deals}
          isDealAmountValid={props.isDealAmountValid}
          validateDealAmount={props.validateDealAmount}
          refundClickHandler={props.refundClickHandler}
          refundFetching={props.refundFetching}
          dealsToCompensate={props.dealsToCompensate}
          onCompensation={props.onCompensation}
          compensationUpdated={props.compensationUpdated}
          compensationCreated={props.compensationCreated}
          onCompensationCreated={props.onCompensationCreated}
        />
      }
      ads={props.adsMessage !== "" && <AdsBlock />}
    >
      <Tour
        step={[
          "stareAtTheChart",
          "showChart",
          "chartWithProfit",
          "chartWithDeals"
        ]}
        originalRef={chartRef}
        disabledTooltip={true}
        withoutStyles={true}
        showTimer
      >
        {({
          next,
          isOriginal,
          isTourActive,
          isTourMode,
          deal,
          step,
          tour,
          startPointsLength
        }) =>
          isOriginal ? (
            !isTourActive &&
            (isStockClose && !isTourMode ? (
              <StockClose
                stocks={props.stocks}
                onStockClick={props.onRecommendedStockClick}
                activeAccount={props.activeAccount}
                stock={props.stock}
              />
            ) : (
              <ChartArea
                animated={props.animated}
                forwardedRef={chartRef}
                points={isTourMode ? tour.points : void 0}
                visuals="mobile"
                intent={null}
                isIOS={props.isIOS}
                stock={props.stock}
                timeUntilNextLockToggle={props.timeUntilNextLockToggle}
                deals={props.deals}
                refundClickHandler={closeDealClickMobileHandler}
                refundFetching={props.refundFetching}
                time={props.time}
              />
            ))
          ) : step === "showChart" ? (
            <ChartArea
              animated={props.animated}
              visuals="mobile"
              points={tour.points}
              deals={{
                type: "SPECIAL TOUR DEAL",
                deals: tour.deals
              }}
              intent={null}
              isIOS={props.isIOS}
              stock={props.stock}
              timeUntilNextLockToggle={props.timeUntilNextLockToggle}
              settings={{
                sentiments: false
              }}
              refundClickHandler={closeDealClickMobileHandler}
              refundFetching={props.refundFetching}
              time={props.time}
            />
          ) : (
            <React.Fragment>
              <ChartArea
                interactive={false}
                animated={props.animated}
                points={tour.points}
                visuals="mobile"
                deals={{
                  type: "SPECIAL TOUR DEAL",
                  deals: tour.deals
                    .slice(-1)
                    .filter(
                      d => startPointsLength - tour.points.length + d.bTime >= 0
                    )
                }}
                intent={null}
                isIOS={props.isIOS}
                stock={props.stock}
                timeUntilNextLockToggle={Infinity}
                onEditIndicator={() => void 0}
                settings={{
                  indicators: [],
                  timeSpan: (60 * 1000) / 0.8,
                  sentiments: false
                }}
                refundClickHandler={closeDealClickMobileHandler}
                refundFetching={props.refundFetching}
                time={props.time}
              />
              {startPointsLength - tour.points.length + tour.deals[0].bTime <
                0 && (
                <StatelessNews
                  news={[
                    {
                      payload: {
                        op: "operationClosed",
                        id: tour.deals[0].id,
                        stock: tour.deals[0].stock,
                        user_id: 1,
                        profit:
                          tour.deals[0].amount +
                          tour.deals[0].amount * (tour.deals[0].percent / 100),
                        accountId: 1,
                        real: 0,
                        balance: 1,
                        turnover: 0,
                        targetTurnover: false,
                        isBidUp: true,
                        close_time: tour.deals[0].timestamp
                      },
                      isDismissed: false
                    }
                  ]}
                  onDismiss={() => null}
                  soundsEnabled={false}
                  mobile={true}
                  tour={true}
                />
              )}
            </React.Fragment>
          )
        }
      </Tour>
    </MobileLayout>
  );
};

function OccasionalPopup(props: LayoutProps) {
  if (props.showInsufficientFundsMessage) {
    // A/B test
    return props.userId % 2 ? (
      <PopupDeposit
        onRefillDemoClick={props.refillDemoAccount}
        onDismiss={props.dismissInsufficientFundsMessage}
      />
    ) : (
      <InsufficientPopup
        onRefillDemoClick={props.refillDemoAccount}
        onDismiss={props.dismissInsufficientFundsMessage}
      />
    );
  }

  if (props.showVerificationRequired) {
    return (
      <VerificationRequiredPopup
        onDismiss={props.dismissVerificationRequiredMessage}
      />
    );
  }

  if (props.showIncreaseBidLimit !== false) {
    return (
      <IncreaseBidLimitPopup onDismiss={props.dismissIncreaseBidLimitMessage} />
    );
  }

  if (props.showTradeIsClosing) {
    return (
      <TradeIsClosingPopup
        onDismiss={props.dismissTradeIsClosing}
        time={props.timeUntilNextLockToggle}
      />
    );
  }

  if (props.showDemoTournament && props.demoTournament) {
    return (
      <DemoTournamentPopup
        tournament={props.demoTournament}
        onDismiss={props.dismissDemoTournament}
      />
    );
  }

  return null;
}

function _MobileLayout({ history, match, location, ...props }: LayoutProps) {
  // Я вынес <MobileTradeScreen/> чтобы лучше
  // считывалось намерение отображать разницу
  // между состояниями подключенния. Если это
  // не удобно, то можно внести код обратно.

  return (
    <React.Fragment>
      <MobileTradeScreen {...props} />
      <News mobile={true} />
      <Route path="/trade/tools" component={Tools} />
      <Route path="/trade/assets" component={Assets} />
      <Route path="/trade/social" component={SocialTradingMobile} />
      <Route path="/sightseeing/tools" component={Tools} />
      <Route path="/sightseeing/assets" component={Assets} />
      <OccasionalPopup
        {...props}
        history={history}
        match={match}
        location={location}
      />
    </React.Fragment>
  );
}

export default withErrorCatch(withRouter(connect()(_MobileLayout)));



// WEBPACK FOOTER //
// ./src/components/Trade/Mobile.js